import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorStore } from '@/store'
import type { DataFormat, Theme } from '@/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ConvertFormatModal,
  CodeGenModal,
  ExportModal,
  ShareModal,
  ValidationModal,
  DiffModal
} from './FeatureModals'

const formats: DataFormat[] = ['auto', 'json', 'yaml', 'xml', 'toml', 'edi', 'ofx', 'cnab']
const themes: Theme[] = ['light', 'dark', 'system']
const languages = ['en', 'pt', 'es']

export default function Toolbar() {
  const { t, i18n } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const {
    format,
    setFormat,
    theme,
    setTheme,
    layoutDirection,
    setLayoutDirection,
    showMinimap,
    toggleMinimap,
    showControls,
    toggleControls,
    parseAndRender,
    content,
    nodes,
  } = useEditorStore()

  // Modal states
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [showCodeGenModal, setShowCodeGenModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [showDiffModal, setShowDiffModal] = useState(false)
  const [showToolsMenu, setShowToolsMenu] = useState(false)

  const handleFormatChange = (value: string) => {
    setFormat(value as DataFormat)
    parseAndRender()
  }

  const handleThemeChange = (value: string) => {
    setTheme(value as Theme)
  }

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value)
  }

  const handleLayoutToggle = () => {
    setLayoutDirection(layoutDirection === 'LR' ? 'TB' : 'LR')
  }

  const handleCopy = async () => {
    if (content) {
      await navigator.clipboard.writeText(content)
    }
  }

  const handlePaste = async () => {
    const text = await navigator.clipboard.readText()
    useEditorStore.getState().setContent(text)
    parseAndRender()
  }

  const handleClear = () => {
    useEditorStore.getState().setContent('')
    parseAndRender()
  }

  const handleFileImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      if (text) {
        // Detect format by extension
        const ext = file.name.split('.').pop()?.toLowerCase()
        let detectedFormat: DataFormat = 'auto'
        if (ext === 'json') detectedFormat = 'json'
        else if (ext === 'yaml' || ext === 'yml') detectedFormat = 'yaml'
        else if (ext === 'xml') detectedFormat = 'xml'
        else if (ext === 'toml') detectedFormat = 'toml'
        else if (ext === 'edi' || ext === 'x12' || ext === 'edifact') detectedFormat = 'edi'
        else if (ext === 'ofx' || ext === 'qfx') detectedFormat = 'ofx'
        else if (ext === 'cnab' || ext === 'rem' || ext === 'ret') detectedFormat = 'cnab'

        useEditorStore.getState().setContent(text)
        setFormat(detectedFormat)
        parseAndRender()
      }
    }
    reader.readAsText(file)
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  return (
    <>
      <header className="flex items-center justify-between px-4 py-3 border-b bg-white dark:bg-slate-900">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <svg
              className="w-8 h-8 text-primary-600"
              viewBox="0 0 32 32"
              fill="none"
            >
              <rect x="2" y="2" width="28" height="28" rx="6" fill="currentColor" fillOpacity="0.1"/>
              <path
                d="M8 12h4v4H8v-4zM14 8h4v4h-4V8zM20 12h4v4h-4v-4zM14 16h4v4h-4v-4zM8 20h4v4H8v-4zM20 20h4v4h-4v-4z"
                fill="currentColor"
              />
            </svg>
            <span className="text-xl font-bold text-slate-900 dark:text-white">
              {t('app.name')}
            </span>
          </div>
        </div>

        {/* Center controls */}
        <div className="flex items-center gap-2">
          {/* Format selector */}
          <Select value={format} onValueChange={handleFormatChange}>
            <SelectTrigger className="w-32 h-8 text-sm">
              <SelectValue placeholder={t('format.auto')} />
            </SelectTrigger>
            <SelectContent>
              {formats.map((f) => (
                <SelectItem key={f} value={f}>
                  {t(`format.${f}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Actions */}
          <div className="flex items-center gap-1 ml-4">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.yaml,.yml,.xml,.toml,.edi,.x12,.edifact,.ofx,.qfx,.cnab,.rem,.ret"
              onChange={handleFileChange}
              className="hidden"
            />

            <button onClick={handleFileImport} className="btn-ghost" title={t('actions.import')}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </button>

            <button onClick={() => setShowExportModal(true)} className="btn-ghost" title={t('actions.export')} disabled={nodes.length === 0}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>

            <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />

            <button onClick={handlePaste} className="btn-ghost" title={t('actions.paste')}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </button>

            <button onClick={handleCopy} className="btn-ghost" title={t('actions.copy')}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>

            <button onClick={handleClear} className="btn-ghost" title={t('actions.clear')}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>

            <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />

            {/* Share button */}
            <button onClick={() => setShowShareModal(true)} className="btn-ghost" title={t('actions.share', 'Share')}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>

          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-2" />

          {/* Tools dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowToolsMenu(!showToolsMenu)}
              className="btn-ghost flex items-center gap-1"
              title={t('tools.menu', 'Tools')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm">{t('tools.menu', 'Tools')}</span>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showToolsMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowToolsMenu(false)} />
                <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg z-20">
                  <button
                    onClick={() => { setShowConvertModal(true); setShowToolsMenu(false) }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    {t('tools.convert', 'Convert Format')}
                  </button>
                  <button
                    onClick={() => { setShowCodeGenModal(true); setShowToolsMenu(false) }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    {t('tools.codegen', 'Generate Code')}
                  </button>
                  <button
                    onClick={() => { setShowValidationModal(true); setShowToolsMenu(false) }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('tools.validate', 'Validate Schema')}
                  </button>
                  <button
                    onClick={() => { setShowDiffModal(true); setShowToolsMenu(false) }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {t('tools.diff', 'Compare Data')}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-2" />

          {/* Layout toggle */}
          <button
            onClick={handleLayoutToggle}
            className="btn-ghost"
            title={layoutDirection === 'LR' ? 'Vertical layout' : 'Horizontal layout'}
          >
            {layoutDirection === 'LR' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            )}
          </button>

          {/* View toggles */}
          <button
            onClick={toggleMinimap}
            className={`btn-ghost ${showMinimap ? 'text-primary-600 dark:text-primary-400' : ''}`}
            title="Toggle minimap"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </button>

          <button
            onClick={toggleControls}
            className={`btn-ghost ${showControls ? 'text-primary-600 dark:text-primary-400' : ''}`}
            title="Toggle controls"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </button>
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          {/* Language selector */}
          <Select value={i18n.language?.split('-')[0] || 'en'} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-28 h-8 text-sm">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang} value={lang}>
                  {t(`language.${lang}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Theme selector */}
          <Select value={theme} onValueChange={handleThemeChange}>
            <SelectTrigger className="w-28 h-8 text-sm">
              <SelectValue placeholder="Theme" />
            </SelectTrigger>
            <SelectContent>
              {themes.map((th) => (
                <SelectItem key={th} value={th}>
                  {t(`theme.${th}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Modals */}
      <ConvertFormatModal isOpen={showConvertModal} onClose={() => setShowConvertModal(false)} />
      <CodeGenModal isOpen={showCodeGenModal} onClose={() => setShowCodeGenModal(false)} />
      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />
      <ValidationModal isOpen={showValidationModal} onClose={() => setShowValidationModal(false)} />
      <DiffModal isOpen={showDiffModal} onClose={() => setShowDiffModal(false)} />
    </>
  )
}
